<template>
  <v-container fluid class="ct-page">
    <page-skeleton v-if="loading" variant="cards" class="mt-4" />

    <template v-else>
      <header class="ct-page__head">
        <p class="ct-page__eyebrow">{{ $t("certificates.eyebrow") }}</p>
        <h1 class="ct-page__title">{{ $t("certificates.title") }}</h1>
        <p class="ct-page__sub">{{ $t("certificates.subtitle") }}</p>
      </header>

      <p v-if="failed" class="ct-page__empty">
        {{ $t("certificates.loadError") }}
      </p>

      <template v-else>
        <section class="ct-card" aria-labelledby="ct-preview-title">
          <h2 id="ct-preview-title" class="ct-card__label">
            {{ $t("certificates.previewTitle") }}
          </h2>

          <template v-if="preview.canIssue">
            <dl class="ct-facts">
              <div class="ct-facts__item">
                <dt>{{ $t("certificates.level") }}</dt>
                <dd>
                  {{ preview.platformLevel }}
                  <span class="ct-facts__cefr">· {{ preview.cefrLevel }}</span>
                </dd>
              </div>
              <div class="ct-facts__item">
                <dt>{{ $t("certificates.since") }}</dt>
                <dd>{{ formatDay(preview.periodStart) }}</dd>
              </div>
              <div class="ct-facts__item">
                <dt>{{ $t("certificates.workload") }}</dt>
                <dd>{{ workloadText(preview) }}</dd>
              </div>
            </dl>
            <p class="ct-card__note">
              {{ $tc("certificates.topicsNote", preview.topicsCount) }}
            </p>

            <div class="ct-sheet" :class="{ 'ct-sheet--busy': !previewImage }">
              <img
                v-if="previewImage"
                class="ct-sheet__img"
                :src="previewImage"
                :alt="$t('certificates.previewImageAlt')"
              />
              <p v-else-if="previewImageFailed" class="ct-sheet__error">
                {{ $t("certificates.previewImageError") }}
              </p>
              <v-progress-circular v-else indeterminate color="primary" />
            </div>
          </template>

          <p v-else class="ct-card__blocked">
            {{ preview.message || $t("certificates.cannotIssue") }}
          </p>

          <v-btn
            class="ct-card__action"
            depressed
            :disabled="!preview.canIssue || issuing"
            :loading="issuing"
            @click="issue"
          >
            {{ $t("certificates.issue") }}
          </v-btn>
        </section>

        <section class="ct-history">
          <h2 class="ct-card__label">{{ $t("certificates.historyTitle") }}</h2>

          <p v-if="!items.length" class="ct-history__empty">
            {{ $t("certificates.historyEmpty") }}
          </p>

          <ul v-else class="ct-history__list">
            <li v-for="item in items" :key="item.id" class="ct-history__row">
              <div class="ct-history__main">
                <span class="ct-history__level">
                  {{ item.platformLevel }} · {{ item.cefrLevel }}
                </span>
                <span class="ct-history__meta">
                  {{
                    $t("certificates.issuedOn", {
                      date: formatInstant(item.issuedAt),
                    })
                  }}
                  · {{ workloadText(item) }}
                  <template v-if="item.verificationCode">
                    · {{ item.verificationCode }}
                  </template>
                </span>
              </div>
              <div class="ct-history__actions">
                <v-btn
                  text
                  small
                  class="ct-history__download"
                  :loading="viewingId === item.id"
                  @click="view(item)"
                >
                  <v-icon left size="18">fi-rr-eye</v-icon>
                  {{ $t("certificates.view") }}
                </v-btn>
                <v-btn
                  text
                  small
                  class="ct-history__download"
                  :loading="downloadingId === item.id"
                  @click="download(item)"
                >
                  <v-icon left size="18">fi-rr-download</v-icon>
                  {{ $t("certificates.download") }}
                </v-btn>
                <v-btn
                  text
                  small
                  class="ct-history__download"
                  :loading="sharingId === item.id"
                  @click="share(item)"
                >
                  <v-icon left size="18">fi-rr-share</v-icon>
                  {{ $t("certificates.share") }}
                </v-btn>
              </div>
            </li>
          </ul>
        </section>
      </template>
    </template>

    <v-dialog
      v-model="viewer.open"
      max-width="960"
      @click:outside="closeViewer"
    >
      <div class="ct-viewer">
        <img
          v-if="viewer.url"
          class="ct-viewer__img"
          :src="viewer.url"
          :alt="$t('certificates.previewImageAlt')"
        />
        <v-btn
          depressed
          class="ct-viewer__close"
          aria-label="close"
          @click="closeViewer"
        >
          {{ $t("certificates.close") }}
        </v-btn>
      </div>
    </v-dialog>
  </v-container>
</template>

<script>
import pitchCustomerApi from "@/shared/pitchCustomerApi";

// Certificados do aluno (card 17trt6jfkw8): ele vê o que será impresso, emite
// quando quiser e baixa de novo qualquer certificado já emitido.
export default {
  name: "CertificatesPage",

  data() {
    return {
      loading: true,
      failed: false,
      issuing: false,
      downloadingId: "",
      sharingId: "",
      viewingId: "",
      preview: { canIssue: false },
      previewImage: "",
      previewImageFailed: false,
      viewer: { open: false, url: "" },
      items: [],
    };
  },

  mounted() {
    this.load();
  },

  beforeDestroy() {
    this.revoke(this.previewImage);
    this.revoke(this.viewer.url);
  },

  methods: {
    revoke(url) {
      if (url) window.URL.revokeObjectURL(url);
    },

    // Prévia visual do que será emitido. Falhar aqui não impede a emissão.
    loadPreviewImage() {
      this.previewImageFailed = false;
      return pitchCustomerApi
        .getCertificatePreviewImage()
        .then((res) => {
          this.revoke(this.previewImage);
          this.previewImage = window.URL.createObjectURL(
            new Blob([res.data], { type: "image/png" })
          );
        })
        .catch(() => {
          this.previewImageFailed = true;
        });
    },

    // Abre a frente de um certificado já emitido, com o conteúdo da época.
    view(item) {
      this.viewingId = item.id;
      return pitchCustomerApi
        .downloadCertificateImage(item.id)
        .then((res) => {
          this.revoke(this.viewer.url);
          this.viewer = {
            open: true,
            url: window.URL.createObjectURL(
              new Blob([res.data], { type: "image/png" })
            ),
          };
        })
        .catch(() => {
          this.alert(this.$t("certificates.previewImageError"), 1);
        })
        .finally(() => {
          this.viewingId = "";
        });
    },

    closeViewer() {
      this.viewer.open = false;
    },

    load() {
      return Promise.all([
        pitchCustomerApi.getCertificatePreview(),
        pitchCustomerApi.getCertificates(),
      ])
        .then(([preview, list]) => {
          this.preview = preview.data?.data || { canIssue: false };
          this.items = list.data?.data || [];
          if (this.preview.canIssue) this.loadPreviewImage();
        })
        .catch(() => {
          this.failed = true;
        })
        .finally(() => {
          this.loading = false;
        });
    },

    // Dia do calendário (AAAA-MM-DD), sem fuso.
    formatDay(value) {
      return String(value || "")
        .slice(0, 10)
        .split("-")
        .reverse()
        .join("/");
    },

    // Instante real da emissão, mostrado no relógio da escola.
    formatInstant(value) {
      return new Date(value).toLocaleDateString(this.$i18n.locale, {
        timeZone: "America/Sao_Paulo",
      });
    },

    // Sem horas (aluno autônomo), o certificado diz "estudo autônomo guiado".
    workloadText(item) {
      return item.workloadHours == null
        ? this.$t("certificates.guidedStudy")
        : this.$tc("certificates.hours", item.workloadHours);
    },

    alert(text, type) {
      this.$root.$emit("display-alert", { text, type });
    },

    saveBlob(blob, filename = "certificado-pitch.pdf") {
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    },

    download(item) {
      this.downloadingId = item.id;
      return pitchCustomerApi
        .downloadCertificate(item.id)
        .then((res) => this.saveBlob(res.data))
        .catch(() => {
          this.alert(this.$t("certificates.downloadError"), 1);
        })
        .finally(() => {
          this.downloadingId = "";
        });
    },

    // Celular e tablet: o menu de compartilhamento do aparelho, com a imagem
    // já anexada. O teste usa um arquivo de mentira porque o navegador precisa
    // ser consultado de forma síncrona, ainda dentro do clique.
    canShareFiles() {
      if (typeof navigator.canShare !== "function") return false;
      if (!window.matchMedia("(pointer: coarse)").matches) return false;
      const probe = new File([""], "certificado-pitch.png", {
        type: "image/png",
      });
      return navigator.canShare({ files: [probe] });
    },

    // Nenhuma rede deixa um site publicar a imagem pelo aluno: a legenda vai
    // para a área de transferência e o aluno confirma a publicação na rede.
    async copyCaption(caption) {
      try {
        await navigator.clipboard.writeText(caption);
        return true;
      } catch (error) {
        return false;
      }
    },

    async share(item) {
      const native = this.canShareFiles();
      // No computador a aba do LinkedIn abre já, durante o clique: depois da
      // espera pela imagem o navegador a trataria como pop-up.
      const tab = native ? null : window.open("", "_blank");
      const caption = this.$t("certificates.shareCaption");
      this.sharingId = item.id;

      try {
        const res = await pitchCustomerApi.downloadCertificateImage(item.id);
        const blob = new Blob([res.data], { type: "image/png" });
        const copied = await this.copyCaption(caption);

        if (native) {
          const file = new File([blob], "certificado-pitch.png", {
            type: "image/png",
          });
          await navigator.share({ files: [file], text: caption });
          if (copied) this.alert(this.$t("certificates.shareCopied"), 0);
          return;
        }

        this.saveBlob(blob, "certificado-pitch.png");
        const composer = `https://www.linkedin.com/feed/?shareActive=true&text=${encodeURIComponent(
          caption
        )}`;
        if (tab) tab.location.href = composer;
        this.alert(
          this.$t(
            copied
              ? "certificates.shareDesktop"
              : "certificates.shareDesktopNoCopy"
          ),
          0
        );
      } catch (error) {
        if (tab) tab.close();
        // Fechar o menu de compartilhamento não é erro.
        if (error?.name === "AbortError") return;
        this.alert(this.$t("certificates.shareError"), 1);
      } finally {
        this.sharingId = "";
      }
    },

    issue() {
      this.issuing = true;
      return pitchCustomerApi
        .issueCertificate()
        .then((res) => {
          const issued = res.data?.data;
          this.items = [issued, ...this.items];
          this.alert(this.$t("certificates.issuedOk"), 0);
          return this.download(issued);
        })
        .catch((error) => {
          // Recusa de regra (422) traz a explicação para o aluno.
          this.alert(
            error?.response?.data?.message ||
              this.$t("certificates.issueError"),
            1
          );
        })
        .finally(() => {
          this.issuing = false;
        });
    },
  },
};
</script>

<style scoped>
.ct-page {
  --ink: var(--dk-ink, #231a2e);
  --muted: var(--dk-muted, #6b6472);
  --line: var(--dk-line, #ece6ef);
  --surface: var(--dk-surface, #fff);
  --pink: var(--dk-pink, #e8276f);
  max-width: 880px;
  margin: 0 auto;
  padding: 24px 12px 56px;
  font-family: "Poppins", sans-serif;
  color: var(--ink);
}

.ct-page__eyebrow {
  margin: 0 0 8px;
  font-size: 0.75rem;
  font-weight: 600;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: var(--pink);
}

.ct-page__title {
  margin: 0;
  font-size: clamp(1.6rem, 4vw, 2.1rem);
  font-weight: 700;
  line-height: 1.2;
}

.ct-page__sub {
  margin: 10px 0 0;
  max-width: 60ch;
  font-size: 0.95rem;
  line-height: 1.6;
  color: var(--muted);
}

.ct-page__head {
  margin-bottom: 28px;
}

.ct-page__empty,
.ct-history__empty {
  color: var(--muted);
}

.ct-card {
  padding: 24px;
  border: 1px solid var(--line);
  border-radius: 16px;
  background: var(--surface);
}

.ct-card__label {
  margin: 0 0 16px;
  font-size: 0.8rem;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--muted);
}

.ct-facts {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(190px, 1fr));
  gap: 16px 24px;
  margin: 0;
}

.ct-facts__item dt {
  font-size: 0.78rem;
  color: var(--muted);
}

.ct-facts__item dd {
  margin: 4px 0 0;
  font-size: 1.05rem;
  font-weight: 600;
}

.ct-facts__cefr {
  color: var(--pink);
}

.ct-card__note {
  margin: 16px 0 0;
  font-size: 0.85rem;
  color: var(--muted);
}

.ct-card__blocked {
  margin: 0;
  font-size: 0.95rem;
  line-height: 1.6;
}

.ct-card__action.v-btn {
  margin-top: 24px;
  height: 44px;
  padding: 0 24px;
  border-radius: 12px;
  font-family: "Poppins", sans-serif;
  font-weight: 600;
  letter-spacing: 0;
  text-transform: none;
  color: #fff;
  background-color: var(--pink) !important;
}

.ct-card__action.v-btn.v-btn--disabled {
  opacity: 0.45;
}

.ct-sheet {
  margin-top: 20px;
  border: 1px solid var(--line);
  border-radius: 12px;
  overflow: hidden;
  background: #fff;
}

.ct-sheet--busy {
  display: flex;
  align-items: center;
  justify-content: center;
  aspect-ratio: 297 / 210;
}

.ct-sheet__img,
.ct-viewer__img {
  display: block;
  width: 100%;
  height: auto;
}

.ct-sheet__error {
  margin: 0;
  padding: 0 16px;
  font-size: 0.85rem;
  color: var(--muted);
}

.ct-viewer {
  position: relative;
  background: #fff;
}

.ct-viewer__close.v-btn {
  position: absolute;
  top: 12px;
  right: 12px;
  font-family: "Poppins", sans-serif;
  font-weight: 600;
  letter-spacing: 0;
  text-transform: none;
}

.ct-history {
  margin-top: 36px;
}

.ct-history__list {
  margin: 0;
  padding: 0;
  list-style: none;
  border-top: 1px solid var(--line);
}

.ct-history__row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 14px 0;
  border-bottom: 1px solid var(--line);
}

.ct-history__main {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.ct-history__level {
  font-weight: 600;
}

.ct-history__meta {
  font-size: 0.82rem;
  color: var(--muted);
}

.ct-history__actions {
  display: flex;
  flex: none;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 4px;
}

.ct-history__download.v-btn {
  flex: none;
  font-family: "Poppins", sans-serif;
  font-weight: 600;
  letter-spacing: 0;
  text-transform: none;
  color: var(--pink);
}

@media (max-width: 600px) {
  .ct-card {
    padding: 20px 16px;
  }

  .ct-card__action.v-btn {
    width: 100%;
  }
}
</style>
